/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { theme } from '../../styles/theme'
import { Product } from '../../types'
import { Button } from '../UI/Button'

interface ProductCardProps {
  product: Product
  isAffiliate: boolean
  referralCode?: string
  onSubscribe: (product: Product) => void
  onOpenCalculator: (strategyName: string, cagr: number) => void
}

const cardStyles = css`
  border-radius: ${theme.borderRadius.default};
  overflow: hidden;
  box-shadow: ${theme.shadows.card};
  transition: ${theme.transitions.transform}, ${theme.transitions.boxShadow};
  margin-bottom: 30px;
  height: 100%;
  position: relative;
  background-color: white;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.cardHover};
  }
`

const headerStyles = css`
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  text-align: center;
  border-bottom: 1px solid #eee;
`

const titleStyles = css`
  font-weight: 700;
  margin-bottom: 10px;
  font-size: 1.5rem;
`

const priceStyles = css`
  font-size: 2.2rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  
  small {
    font-size: 0.9rem;
    color: #6c757d;
  }
`

const bodyStyles = css`
  padding: 30px 20px;
`

const featuresListStyles = css`
  list-style: none;
  padding: 0;
  margin-bottom: 30px;
  
  li {
    padding: 10px 0;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
    
    &:last-child {
      border-bottom: none;
    }
    
    i {
      color: ${theme.colors.primary};
      margin-right: 10px;
      min-width: 20px;
      text-align: center;
    }
  }
`

const tagStyles = css`
  position: absolute;
  top: 0;
  color: white;
  padding: 2px 15px;
  font-weight: 600;
  font-size: 0.8rem;
  z-index: 2;
`

const recommendedTagStyles = css`
  ${tagStyles}
  right: 0;
  background-color: ${theme.colors.success};
  border-bottom-left-radius: ${theme.borderRadius.default};
`

const affiliateTagStyles = css`
  ${tagStyles}
  left: 0;
  background-color: #4a6bff;
  border-bottom-right-radius: ${theme.borderRadius.default};
`

const referralTagStyles = css`
  ${tagStyles}
  left: 0;
  background-color: ${theme.colors.success};
  border-bottom-right-radius: ${theme.borderRadius.default};
`

const linksStyles = css`
  text-align: center;
  margin-bottom: 20px;
  
  p {
    margin-bottom: 5px;
  }
  
  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`

const disclaimerStyles = css`
  margin-bottom: 20px;
  
  .small {
    font-size: 0.875rem;
    color: #6c757d;
    line-height: 1.4;
  }
`

export const ProductCard = ({ 
  product, 
  isAffiliate, 
  referralCode, 
  onSubscribe, 
  onOpenCalculator 
}: ProductCardProps) => {
  const handleSubscribeClick = () => {
    onSubscribe(product)
  }

  const handleCalculatorClick = () => {
    const cagrMatch = product.features[0].text.match(/[\d.]+/)
    const cagr = cagrMatch ? parseFloat(cagrMatch[0]) : 0
    onOpenCalculator(product.name, cagr)
  }

  const featuresHTML = product.features.map((feature, index) => (
    <li key={index}>
      <i className={feature.included ? "fas fa-check-circle" : "fas fa-times-circle text-muted"}></i>
      {feature.text}
    </li>
  ))

  return (
    <div className="col-md-6 col-lg-4 product-container">
      <div className="product" css={cardStyles}>
        {product.recommended && (
          <div css={recommendedTagStyles}>RECOMMENDED</div>
        )}
        {isAffiliate && (
          <div css={affiliateTagStyles}>1 YEAR FREE</div>
        )}
        {!isAffiliate && referralCode && (
          <div css={referralTagStyles}>2 MONTHS FREE</div>
        )}
        
        <div css={headerStyles}>
          <h3 className="product-name" css={titleStyles}>{product.name}</h3>
          <div className="product-price" css={priceStyles}>
            ${product.price.toFixed(2)} <small>/month</small>
          </div>
        </div>
        
        <div css={bodyStyles}>
          <ul css={featuresListStyles}>
            {featuresHTML}
          </ul>

          <div css={linksStyles}>
            <p>
              <a 
                href={`/execution-metrics/${product.reportSlug}`}
                target="_blank" 
                rel="noopener noreferrer"
              >
                View Execution Metrics
              </a>
            </p>
            <p>
              <a 
                href={`/performance-metrics/${product.reportSlug}`}
                target="_blank" 
                rel="noopener noreferrer"
              >
                View Performance Report
              </a>
            </p>
            <p>
              <a href="#" onClick={(e) => { e.preventDefault(); handleCalculatorClick(); }}>
                Investment Calculator
              </a>
            </p>
          </div>

          <div css={disclaimerStyles}>
            <p className="small text-muted">
              Performance metrics shown are based on back tested results of daily close prices from{' '}
              <strong>{product.reportBeginDate}</strong> to <strong>{product.reportEndDate}</strong>. 
              Most recent metrics can be obtained by clicking on the View Performance Report link above. 
              Back test results may not reflect actual trading performance, and past performance may not be indicative of future results. 
              All strategies rebalance at most once a day, a few minutes before the market closes.
              Alerts are automatically posted to the Telegram channel as soon as a rebalance event occurs.
              Trading strategy alerts are for informational purposes only and should not be considered financial advice.
            </p>
          </div>
          
          <Button 
            variant="subscribe"
            data-bs-toggle="modal" 
            data-bs-target="#subscribeModal"
            onClick={handleSubscribeClick}
          >
            Subscribe Now
          </Button>
        </div>
      </div>
    </div>
  )
}